"use client"

import { useEffect, useRef, useState, type ReactNode } from "react"
import { Button } from "@/components/ui/controls/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/layout/card"
import { Activity, ExternalLink, WifiOff } from "lucide-react"
import { useToast } from "@/lib/hooks/use-toast"
import apiClient from "@/lib/api/api-client"
import { isCapacitorNative } from "@/lib/capacitor"
import {
  currentAppReturnPath,
  OAUTH_RETURN_EVENT,
  openExternalAuth,
  type OAuthReturnDetail,
} from "@/lib/capacitor/oauth-browser"

interface WhoopConnectButtonProps {
  playerId: string
  onSuccess?: () => void
  className?: string
  children?: ReactNode
  /** Start the sync and return before it finishes. */
  backgroundSync?: boolean
  onError?: () => void
}

export function WhoopConnectButton({ playerId, onSuccess, className, children, backgroundSync = false, onError }: WhoopConnectButtonProps) {
  const [isConnecting, setIsConnecting] = useState(false)
  const { toast } = useToast()
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const finishingRef = useRef(false)
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }

  const finishConnected = async () => {
    if (finishingRef.current) return
    finishingRef.current = true
    stopPolling()
    toast({ title: "WHOOP connected", description: "Starting initial sync..." })
    if (backgroundSync) {
      void apiClient.whoop.sync({ playerId }).catch(() => {})
    } else {
      try {
        await apiClient.whoop.sync({ playerId })
      } catch {
        // ignore sync error, still proceed
      }
    }
    onSuccess?.()
    setIsConnecting(false)
    finishingRef.current = false
  }

  const startPolling = () => {
    stopPolling()
    let attempts = 0
    const maxAttempts = isCapacitorNative() ? 80 : 10
    pollRef.current = setInterval(async () => {
      attempts += 1
      try {
        const w = await apiClient.whoop.getByPlayer(playerId)
        if (w?.isConnected) {
          await finishConnected()
          return
        }
      } catch {
        // keep polling
      }
      if (attempts >= maxAttempts) {
        stopPolling()
        setIsConnecting(false)
        onErrorRef.current?.()
      }
    }, 2000)
  }

  useEffect(() => {
    const onOAuthReturn = (event: Event) => {
      const detail = (event as CustomEvent<OAuthReturnDetail>).detail
      if (detail?.provider !== "whoop") return
      if (detail.playerId && detail.playerId !== playerId) return
      if (detail.error) {
        stopPolling()
        setIsConnecting(false)
        toast({
          title: "WHOOP connection failed",
          description: "Authorization was cancelled or did not complete. Try again.",
          variant: "destructive",
        })
        onErrorRef.current?.()
        return
      }
      void finishConnected()
    }

    window.addEventListener(OAUTH_RETURN_EVENT, onOAuthReturn)
    return () => {
      window.removeEventListener(OAUTH_RETURN_EVENT, onOAuthReturn)
      stopPolling()
    }
  }, [playerId])

  const handleConnect = async () => {
    try {
      setIsConnecting(true)
      finishingRef.current = false
      const native = isCapacitorNative()
      const res = await apiClient.whoop.connect({
        playerId,
        ...(native
          ? { client: "native", returnPath: currentAppReturnPath() }
          : {}),
      })
      const url = res.authUrl

      if (url) {
        await openExternalAuth(url)
        toast({
          title: "Complete WHOOP authorization",
          description: native
            ? "Sign in if asked. You will return here automatically when it finishes."
            : "A new window was opened. After authorizing, return here.",
        })
        startPolling()
      } else {
        setIsConnecting(false)
      }
    } catch (e: unknown) {
      setIsConnecting(false)
      const message = e instanceof Error ? e.message : "Unexpected error"
      toast({
        title: "Failed to start connection",
        description: message,
        variant: "destructive",
      })
      onErrorRef.current?.()
    }
  }

  return (
    <Button onClick={() => void handleConnect()} disabled={isConnecting} className={className}>
      {isConnecting ? (
        <>
          <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          Connecting...
        </>
      ) : (
        <>
          <ExternalLink className="mr-2 h-4 w-4" />
          {children || "Connect WHOOP"}
        </>
      )}
    </Button>
  )
}

interface WhoopNoConnectionProps {
  playerId: string
  onSuccess?: () => void
}

export function WhoopNoConnection({ playerId, onSuccess }: WhoopNoConnectionProps) {
  return (
    <div className="space-y-6">
      <div className="text-center py-8">
        <WifiOff className="h-16 w-16 text-gray-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">No WHOOP connection</h2>
        <p className="text-gray-600 mb-6">Connect your WHOOP account to view insights and trends</p>
        <WhoopConnectButton playerId={playerId} onSuccess={onSuccess} className="text-lg px-6 py-3">
          Connect WHOOP
        </WhoopConnectButton>
      </div>

      <Card className=" shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            What you'll get after connecting
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
            <li>Recovery, sleep, and strain trends</li>
            <li>Strain breakdown by workout — sport, duration, and heart rate</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  )
}
